import { css } from '@emotion/css';

import { type GrafanaTheme2, renderMarkdown } from '@grafana/data';
import { t } from '@grafana/i18n';
import { useFlagGrafanaVisualDesignRefresh } from '@grafana/runtime/internal';
import { Box, IconButton, useStyles2 } from '@grafana/ui';

import { type Spec } from '../api/clients/banners/v0alpha1';

type Props = Pick<Spec, 'message' | 'variant'> & {
  // Renders a close button when provided
  onDismiss?: () => void;
};

export function Banner({ message, variant, onDismiss }: Props) {
  const visualRefreshEnabled = useFlagGrafanaVisualDesignRefresh();
  const styles = useStyles2(getStyles, variant, visualRefreshEnabled);

  return (
    <Box
      display="flex"
      backgroundColor={variant}
      borderRadius={visualRefreshEnabled ? 'lg' : 'default'}
      borderStyle={visualRefreshEnabled ? 'solid' : undefined}
      borderColor={visualRefreshEnabled ? variant : undefined}
      paddingY={1}
      paddingX={2}
      gap={2}
      alignItems="center"
    >
      <div className={styles.content}>
        <div dangerouslySetInnerHTML={{ __html: renderMarkdown(message) }} />
      </div>
      {onDismiss && (
        <IconButton
          name="times"
          onClick={onDismiss}
          aria-label={t('banner.dismiss.tooltip-close', 'Close banner')}
          className={styles.dismiss}
        />
      )}
    </Box>
  );
}

function getStyles(theme: GrafanaTheme2, variant: Props['variant'], visualRefreshEnabled: boolean) {
  return {
    content: css({
      flexGrow: 1,
      color: visualRefreshEnabled ? theme.colors[variant].textEmphasis : undefined,
      p: {
        margin: 0,
      },
      a: {
        color: theme.colors.text.link,
      },
    }),
    // The X glyph is inset 4px within its icon, so pull the button out by that much (replacing its
    // own 4px right margin) to put the X as far from the edge as the message is from the other one
    dismiss: css({
      marginRight: theme.spacing(-0.5),
    }),
  };
}
